import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Buildings, MapPin, GraduationCap, ArrowLeft, Sparkle, UsersThree,
  Leaf, Plant, BookOpen, ArrowRight, Trophy, Flask, ChalkboardSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Kind → friendly noun + primary icon.
 * Broad scope: covers schools, colleges, universities, polytechnics,
 * institutes, academies, training centres, open universities, research
 * labs, extension centres, alumni chapters and misc educational bodies.
 */
const KIND_LABEL = {
  school:            { noun: "School",             icon: GraduationCap, hub: "School Hub"                },
  college:           { noun: "College",            icon: GraduationCap, hub: "Campus Hub"                },
  university:        { noun: "University",         icon: Buildings,     hub: "University Hub"            },
  polytechnic:       { noun: "Polytechnic",        icon: Buildings,     hub: "Polytechnic Hub"           },
  institute:         { noun: "Institute",          icon: Buildings,     hub: "Institute Hub"             },
  academy:           { noun: "Academy",            icon: Trophy,        hub: "Academy Hub"               },
  training_center:   { noun: "Training Centre",    icon: ChalkboardSimple, hub: "Training Hub"           },
  open_university:   { noun: "Open University",    icon: Buildings,     hub: "Open University Hub"       },
  research_lab:      { noun: "Research Lab",       icon: Flask, hub: "Research Hub"            },
  extension_center:  { noun: "Extension Centre",   icon: Plant,         hub: "Extension Hub"             },
  alumni_chapter:    { noun: "Alumni Chapter",     icon: UsersThree,    hub: "Alumni Hub"                },
  other_educational: { noun: "Educational Institution", icon: Buildings, hub: "Institution Hub"          },
};
const kindMeta = (k) => KIND_LABEL[k] || KIND_LABEL.other_educational;

/**
 * OrgLanding — /org/:slug
 *
 * Public landing page for a school / college / university / alumni chapter.
 * When the Hub is dormant, shows an "Activate NaturePulse Hub" prompt
 * that any signed-in teacher/admin can click.
 */
export default function OrgLanding() {
  const { slug } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, org: null, count: 0 });
  const [linking, setLinking] = useState(false);
  const [activating, setActivating] = useState(false);

  const loadOrg = () => {
    http.get(`/orgs/${slug}`)
      .then((r) => setState({ loading: false, org: r.data.org, count: r.data.member_count }))
      .catch(() => setState({ loading: false, org: null, count: 0 }));
  };
  useEffect(() => { loadOrg(); /* eslint-disable-next-line */ }, [slug]);

  const activateHub = async () => {
    if (!user) return toast.error("Sign in to activate the Hub");
    setActivating(true);
    try {
      await http.post(`/orgs/${slug}/activate`);
      toast.success("Hub activated!");
      loadOrg();
      // Auto-navigate to the fresh hub dashboard.
      setTimeout(() => navigate(`/org/${slug}/hub`), 400);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't activate");
    } finally { setActivating(false); }
  };

  const linkAs = async (role) => {
    if (!user) return toast.error("Sign in first");
    setLinking(true);
    try {
      await http.post("/orgs/mine/link", { org_id: state.org.id, role });
      toast.success(`Linked as ${role}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't link");
    } finally { setLinking(false); }
  };

  if (state.loading) {
    return <main className="min-h-screen pt-32 md:pt-36 px-5 md:px-10 bg-[#F5EBDC]"><div className="max-w-4xl mx-auto text-[#3A3830]">Loading…</div></main>;
  }
  if (!state.org) {
    return (
      <main className="min-h-screen pt-32 md:pt-36 px-5 md:px-10 bg-[#F5EBDC]">
        <div className="max-w-2xl mx-auto text-center py-16">
          <Buildings size={40} weight="duotone" className="mx-auto text-[#B85F2A]" />
          <h1 className="mt-4 font-serif-display text-4xl">Institution not found</h1>
          <p className="mt-2 text-[#3A3830]">This educational institution hasn't joined NaturePulse yet.</p>
          <Link to="/" className="mt-6 inline-block rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2.5 text-sm">
            <ArrowLeft size={12} weight="bold" className="inline mr-1" /> Back home
          </Link>
        </div>
      </main>
    );
  }

  const o = state.org;
  const meta = kindMeta(o.kind);
  const KindIcon = meta.icon;
  const location = [o.city, o.state, o.country].filter(Boolean).join(" · ");

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid="org-landing">
      <div className="max-w-5xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]">
          <ArrowLeft size={11} weight="bold" /> Home
        </Link>

        {/* Header */}
        <header className="mt-6 grid md:grid-cols-[auto_1fr] gap-6 items-start">
          <div className="h-24 w-24 rounded-2xl bg-white border border-black/10 flex items-center justify-center overflow-hidden shrink-0">
            {o.logo_url ? (
              <img src={o.logo_url} alt={o.name} className="w-full h-full object-contain" />
            ) : (
              <KindIcon size={40} weight="duotone" className="text-[#B85F2A]" />
            )}
          </div>
          <div>
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A] flex items-center gap-1.5">
              <Sparkle size={11} weight="fill" /> NaturePulse · {meta.hub}
            </div>
            <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight">{o.name}</h1>
            {o.short_name && <div className="mt-1 text-[#3A3830]">{o.short_name}{o.established_year ? ` · est. ${o.established_year}` : ""}</div>}
            <div className="mt-2 flex flex-wrap gap-3 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
              {location && (<span className="inline-flex items-center gap-1"><MapPin size={10} weight="fill" /> {location}</span>)}
              <span className="inline-flex items-center gap-1"><UsersThree size={10} weight="fill" /> {state.count} member{state.count !== 1 ? "s" : ""}</span>
              <span className="inline-flex items-center gap-1"><Buildings size={10} weight="fill" /> {meta.noun}</span>
              {o.verified && <span className="inline-flex items-center gap-1 text-emerald-700"><Sparkle size={10} weight="fill" /> Verified</span>}
            </div>
            {o.active && (
              <div className="mt-4 flex flex-wrap gap-2">
                <Link
                  to={`/org/${slug}/hub`}
                  data-testid="org-open-hub"
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#1B4332] text-[#F5EBDC] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:opacity-90"
                >
                  Open Hub <ArrowRight size={11} weight="bold" />
                </Link>
                {(o.country || o.city) && (
                  <Link
                    to={`/geo?${new URLSearchParams(Object.fromEntries(
                      Object.entries({ country: o.country, state: o.state, district: o.district, city: o.city })
                        .filter(([, v]) => v)
                    )).toString()}`}
                    data-testid="org-geo-tree"
                    className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:border-[#B85F2A] hover:text-[#B85F2A]"
                  >
                    <MapPin size={11} weight="fill" /> On the map
                  </Link>
                )}
                <ManageOnAlumniButton org={o} slug={slug} />
              </div>
            )}
          </div>
        </header>

        {/* Dormant Hub prompt — one-tap activation for any signed-in user */}
        {!o.active && (
          <section className="mt-8 rounded-2xl border-2 border-dashed border-[#B85F2A]/40 bg-white/70 p-6 md:p-8" data-testid="org-hub-dormant">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A]">Hub Dormant</div>
            <h2 className="mt-1 font-serif-display text-2xl md:text-3xl">Activate the NaturePulse {meta.hub} for {o.short_name || o.name}.</h2>
            <p className="mt-2 text-[#3A3830] max-w-2xl">
              One teacher, faculty member, researcher or admin taps <strong>Activate</strong>.
              Then students, teachers, researchers and alumni can join, take part in Field Tasks, run a Garden, and build the institution's living nature record.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={activateHub}
                disabled={activating || !user}
                data-testid="org-activate-hub"
                className="rounded-full bg-[#B85F2A] text-white px-4 py-2 text-sm font-mono-tech tracking-[0.24em] uppercase disabled:opacity-50"
              >
                {activating ? "Activating…" : `Activate ${meta.hub}`}
              </button>
              {!user && (
                <span className="inline-flex items-center font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
                  Sign in first <ArrowRight size={10} weight="bold" className="inline ml-1" />
                </span>
              )}
              <Link
                to="/farmers/almanac"
                className="rounded-full border border-black/20 bg-white px-4 py-2 text-sm font-mono-tech tracking-[0.24em] uppercase hover:bg-black/5"
              >
                Read the vision →
              </Link>
            </div>
          </section>
        )}

        {/* Join as… */}
        {user && (
          <section className="mt-8 rounded-2xl border border-black/10 bg-white p-6" data-testid="org-join">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#B85F2A]">Join this institution</div>
            <h3 className="mt-1 font-serif-display text-xl">Pick your role at {o.short_name || o.name}</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {["student", "teacher", "researcher", "alumni", "mentor"].map((r) => (
                <button
                  key={r}
                  disabled={linking}
                  onClick={() => linkAs(r)}
                  data-testid={`org-link-${r}`}
                  className="rounded-full border border-black/15 bg-white hover:border-[#B85F2A] hover:text-[#B85F2A] px-4 py-2 text-sm font-mono-tech tracking-[0.24em] uppercase disabled:opacity-50"
                >
                  {r}
                </button>
              ))}
            </div>
            <p className="mt-3 text-xs text-[#3A3830]">
              You can hold multiple roles (e.g. alumni + mentor). If your institution is on AlumniPulse or IndiaAlumni, verified roles come across automatically.
            </p>
          </section>
        )}

        {/* Coming soon panels */}
        <section className="mt-8 grid md:grid-cols-3 gap-3">
          <ComingCard icon={Plant} title="Garden Manager" body="Vegetables, herbs and flowers, tracked from seed to harvest." />
          <ComingCard icon={Leaf} title="Nature Around Us" body="A living record of trees, birds, insects, pollinators, weather." />
          <ComingCard icon={BookOpen} title="Then & Now" body="Alumni recollections of nature around campus, across decades." />
        </section>

        {o.departments && o.departments.length > 0 && (
          <section className="mt-8">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830]">Departments</div>
            <div className="mt-2 flex flex-wrap gap-2">
              {o.departments.map((d) => (
                <span key={d} className="rounded-full border border-black/15 bg-white px-3 py-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase">{d}</span>
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}

function ComingCard({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-5">
      <Icon size={22} weight="duotone" className="text-[#B85F2A]" />
      <div className="mt-2 font-serif-display text-lg">{title}</div>
      <p className="mt-1 text-sm text-[#3A3830]">{body}</p>
      <div className="mt-3 font-mono-tech text-[9px] tracking-[0.32em] uppercase text-[#3A3830]">
        Coming next <ArrowRight size={10} weight="bold" className="inline ml-1" />
      </div>
    </div>
  );
}

/**
 * ManageOnAlumniButton — surfaces when the org was synced from AlumniPulse
 * or IndiaAlumni AND the partner API is currently reachable. Click flow:
 * 1. Server-to-server mint of a v2 "manage" handoff via the partner API
 * 2. Redirect the user to AlumniPulse's builder for this institution
 *
 * If the partner isn't configured we quietly render nothing — we don't want
 * to advertise a button that would 503 on click.
 */
function ManageOnAlumniButton({ org, slug }) {
  const [config, setConfig] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    http.get("/partners/manage-alumnipulse/config")
      .then((r) => setConfig(r.data.partners || {}))
      .catch(() => setConfig({}));
  }, []);

  const source = (org.source || "").toLowerCase();
  if (!org.source_id) return null;
  if (!["alumnipulse", "indiaalumni"].includes(source)) return null;
  if (!config) return null; // still loading
  if (!config[source]) return null; // partner not configured yet

  const label = source === "indiaalumni" ? "Manage on IndiaAlumni" : "Manage on AlumniPulse";

  const onClick = async () => {
    setBusy(true);
    try {
      const r = await http.post("/partners/manage-alumnipulse", { org_slug: slug });
      window.location.assign(r.data.handoff_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't reach partner");
      setBusy(false);
    }
  };

  return (
    <button
      onClick={onClick}
      disabled={busy}
      data-testid="org-manage-on-alumni"
      className="inline-flex items-center gap-1.5 rounded-full border border-[#B85F2A]/40 bg-[#B85F2A]/10 text-[#B85F2A] px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-[#B85F2A] hover:text-white transition disabled:opacity-60"
    >
      {busy ? "Handing off…" : label} <ArrowRight size={11} weight="bold" />
    </button>
  );
}

